"use client";

import { useMemo, useState } from "react";
import {
  Panel,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  DocumentActions,
  DocumentPreview,
} from "@/components/tools/research-workflow/document-parts";
import {
  type FieldSpec,
  SpecFields,
} from "@/components/tools/research-workflow/form-parts";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  docToHtml,
  docToMarkdown,
  docToText,
} from "@/lib/tool-kit/research-workflow/document";
import {
  buildInterviewGuide,
  checkGuideQuestions,
  countMainQuestions,
  type InterviewInput,
  type InterviewRecording,
  type InterviewTopic,
} from "@/lib/tool-kit/research-workflow/interview-guide";

/** English strings. A translated content file overrides these through `ui`. */
export const INTERVIEW_GUIDE_GENERATOR_STRINGS = {
  studyPanel: "The study and the interview",
  topicsPanel: "Topics and main questions",
  titleLabel: "Study title",
  titlePlaceholder: "e.g. Becoming a Teacher: The First Year in the Classroom",
  questionLabel: "Research question",
  questionPlaceholder:
    "e.g. How do newly qualified teachers experience their first year in the classroom?",
  aboutLabel: "What the study is about, in a participant's words",
  aboutPlaceholder:
    "e.g. how new teachers experience their first year in the classroom",
  interviewerLabel: "Interviewer's name (optional)",
  interviewerPlaceholder: "e.g. Sam Okafor",
  institutionLabel: "Institution (optional)",
  institutionPlaceholder: "e.g. Lakeside University",
  warmUpLabel: "Extra warm-up questions — one per line (optional)",
  warmUpPlaceholder:
    "e.g. How long have you been teaching at your current school?",
  typeLabel: "Interview type",
  typeSemiOption: "Semi-structured",
  typeStructuredOption: "Structured",
  minutesLabel: "Planned length in minutes (optional)",
  recordingLabel: "Recording",
  recordingAudioOption: "Audio",
  recordingVideoOption: "Video",
  recordingNoneOption: "Notes only",
  topicNameLabel: "Topic {number}",
  topicNamePlaceholder: "e.g. support from colleagues",
  topicQuestionsLabel:
    "Main questions for topic {number} — one per line, or leave blank for templates",
  topicQuestionsPlaceholder:
    "e.g. Tell me about the support you have had from colleagues this year.",
  addTopic: "Add a topic",
  removeTopic: "Remove",
  removeTopicLabel: "Remove topic {number}",
  summaryHeading: "Question check",
  questionCount: "one:{count} main question|other:{count} main questions",
  summaryTime: "{questions} in {minutes} minutes — {per}.",
  perQuestion:
    "one:about {count} minute for each|other:about {count} minutes for each",
  checksClear:
    "No closed, leading or double-barrelled wording in the questions you wrote. Template questions are written to pass these checks.",
  checksLead: "Questions worth another look:",
  issues: {
    multiple: "Two questions in one — ask them separately.",
    closed:
      "Closed — it can be answered yes or no. Start with “What”, “How” or “Tell me about”.",
    leading: "Leading — it suggests the answer you expect.",
    double: "Contains “and” or “or” — check that it asks about one thing.",
    why: "Starts with “Why”, which can sound like a demand to justify. Try “What led you to…”.",
    long: "Over 25 words — people lose the start of a long question.",
  },
  closedStarters: [
    "do",
    "does",
    "did",
    "is",
    "are",
    "was",
    "were",
    "am",
    "have",
    "has",
    "had",
    "can",
    "could",
    "will",
    "would",
    "should",
    "shall",
    "may",
    "might",
    "don't",
    "doesn't",
    "didn't",
    "isn't",
    "aren't",
    "wasn't",
    "weren't",
    "haven't",
    "hasn't",
    "can't",
    "couldn't",
    "won't",
    "wouldn't",
    "shouldn't",
  ],
  invitationStarters: ["can", "could", "would", "will"],
  openRequests: [
    "tell",
    "describe",
    "explain",
    "talk",
    "walk",
    "share",
    "say",
    "give",
    "think",
    "recall",
    "remember",
  ],
  leadingPhrases: [
    "don't you",
    "wouldn't you",
    "isn't it",
    "isn't that",
    "aren't they",
    "agree that",
    "obviously",
    "surely",
    "of course",
    "clearly",
  ],
  conjunctions: ["and", "or"],
  whyWords: ["why"],
  previewHeading: "Your interview guide",
  previewLabel: "Interview guide preview",
  copyText: "Copy as text",
  copyFormatted: "Copy for Word or Google Docs",
  copied: "Copied!",
  downloadText: "Download .txt",
  downloadMarkdown: "Download .md",
  docTitle: "Interview Guide",
  metaStudy: "Study: {value}",
  metaQuestion: "Research question: {value}",
  metaType: "Interview type: {value}",
  metaLength: "Planned length: about {minutes} minutes",
  typeSemi: "semi-structured",
  typeStructured: "structured",
  beforeHeading: "Before the interview",
  beforeItems: [
    "Re-read the research question and this guide.",
    "Test the recorder or the call's recording, and have a backup.",
    "Have the information sheet and two copies of the consent form ready.",
    "Choose a quiet, private place, or check the call link and the participant's time zone.",
    "Note the participant's code, the date and the setting.",
  ],
  introHeading: "Introduction (read aloud)",
  introThanks: "Thank you for agreeing to talk with me today.",
  introNameInstitution:
    "My name is {interviewer}, and I am a researcher at {institution}.",
  introName: "My name is {interviewer}.",
  introAbout:
    "I am interested in {about}, and I would like to hear about your experiences in your own words.",
  introTime: "The interview will take about {minutes} minutes.",
  introNoRight: "There are no right or wrong answers.",
  introStructured:
    "I will read each question exactly as it is written, so some may sound a little formal — that way everyone I interview hears the same questions.",
  consentHeading: "Consent reminder",
  consentRead:
    "Before we start: you have read the information sheet and signed the consent form — do you have any questions about either?",
  consentVoluntary:
    "Taking part is voluntary. You can skip any question, take a break or stop at any time, without giving a reason.",
  consentAudio:
    "With your permission, I will audio-record our conversation so I can listen properly instead of taking notes. Is that all right?",
  consentVideo:
    "With your permission, I will video-record our conversation so I have an accurate record of it. Is that all right?",
  consentNotes:
    "I will take written notes while we talk, but I will not record the conversation.",
  consentStart: "Shall we begin?",
  warmUpHeading: "Warm-up",
  warmUpDefaults: [
    "To start, could you tell me a little about yourself?",
    "What does a typical week look like for you at the moment?",
  ],
  topicHeading: "Topic {number}: {topic}",
  mainLead: "Main questions:",
  followUpLead:
    "Follow-up questions — build on what the participant just said:",
  mainTemplates: [
    "Can you tell me about your experience of {topic}?",
    "What has stood out to you most about {topic}?",
  ],
  followUpTemplates: [
    "You mentioned … — could you tell me more about that?",
    "How did that compare with what you expected?",
    "What, if anything, would you change about {topic}?",
  ],
  probesHeading: "Probes to use anywhere",
  probesLead: "Use these to draw out detail without suggesting an answer:",
  probes: [
    "Could you give me an example?",
    "What happened next?",
    "How did you feel about that?",
    "What do you mean by “…”?",
    "Could you say a little more about that?",
    "(Pause — give them time to go on.)",
  ],
  structuredHeading: "How to ask the questions",
  structuredLead:
    "Read every question exactly as written and in this order. If the participant hesitates, use only these neutral prompts:",
  structuredProbes: [
    "Repeat the question exactly as written.",
    "“Take your time.”",
    "“Is there anything else?”",
  ],
  closingHeading: "Closing",
  closingItems: [
    "Is there anything else you would like to add that we have not talked about?",
    "Is there anything you would like to ask me?",
    "Thank you very much for your time and for sharing your experiences.",
    "(Stop the recording, if you made one.)",
  ],
  afterHeading: "After the interview",
  afterItems: [
    "Write field notes straight away: the setting, the mood, anything the recording will not capture.",
    "Move the recording to secure storage and delete it from portable devices.",
    "Note which questions worked and which you would change before the next interview.",
  ],
  missingTitle: "[study title]",
  missingQuestion: "[research question]",
  missingAbout: "[what the study is about]",
  missingTopic: "[topic]",
} as const;

type Strings = typeof INTERVIEW_GUIDE_GENERATOR_STRINGS;

const STUDY_FIELDS: FieldSpec<InterviewInput, Strings>[] = [
  ["title", "titleLabel", "titlePlaceholder", false],
  ["researchQuestion", "questionLabel", "questionPlaceholder", true],
  ["about", "aboutLabel", "aboutPlaceholder", false],
  ["interviewer", "interviewerLabel", "interviewerPlaceholder", false],
  ["institution", "institutionLabel", "institutionPlaceholder", false],
  ["warmUp", "warmUpLabel", "warmUpPlaceholder", true],
];

const MAX_TOPICS = 12;

const INITIAL: InterviewInput = {
  title: "",
  researchQuestion: "",
  about: "",
  interviewer: "",
  institution: "",
  type: "semi",
  minutes: "",
  recording: "audio",
  warmUp: "",
  topics: [
    { id: "t1", name: "", questions: "" },
    { id: "t2", name: "", questions: "" },
    { id: "t3", name: "", questions: "" },
  ],
};

export default function InterviewGuideGeneratorWidget() {
  const strings = useToolUi(INTERVIEW_GUIDE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<InterviewInput>(INITIAL);
  const [minutesRaw, setMinutesRaw] = useState("");
  const [nextId, setNextId] = useState(4);

  const set = <K extends keyof InterviewInput>(
    key: K,
    value: InterviewInput[K]
  ) => setInput((current) => ({ ...current, [key]: value }));

  const updateTopic = (id: string, patch: Partial<InterviewTopic>) =>
    setInput((current) => ({
      ...current,
      topics: current.topics.map((topic) =>
        topic.id === id ? { ...topic, ...patch } : topic
      ),
    }));

  const addTopic = () => {
    setInput((current) => ({
      ...current,
      topics: [
        ...current.topics,
        { id: `t${nextId}`, name: "", questions: "" },
      ],
    }));
    setNextId((id) => id + 1);
  };

  const removeTopic = (id: string) =>
    setInput((current) => ({
      ...current,
      topics: current.topics.filter((topic) => topic.id !== id),
    }));

  const minutes = parseNumberInput(minutesRaw);
  const validMinutes = minutes !== null && minutes > 0 ? minutes : null;
  const resolved = useMemo<InterviewInput>(
    () => ({ ...input, minutes: validMinutes ? fmt(validMinutes) : "" }),
    [input, validMinutes, fmt]
  );
  const doc = useMemo(
    () => buildInterviewGuide(resolved, strings),
    [resolved, strings]
  );
  const checks = useMemo(
    () => checkGuideQuestions(input, strings),
    [input, strings]
  );
  const total = countMainQuestions(input, strings);
  const counted = tPlural(strings.questionCount, locale, total, {
    count: fmt(total),
  });
  const per = validMinutes && total > 0 ? validMinutes / total : null;
  const text = useMemo(() => docToText(doc), [doc]);

  return (
    <div className="space-y-6">
      <Panel title={strings.studyPanel}>
        <SpecFields
          input={input}
          onChange={set}
          prefix="igg"
          specs={STUDY_FIELDS}
          strings={strings}
        />
        <PillGroup
          label={strings.typeLabel}
          name="igg-type"
          onChange={(value) => set("type", value)}
          options={[
            { value: "semi", label: strings.typeSemiOption },
            { value: "structured", label: strings.typeStructuredOption },
          ]}
          value={input.type}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="igg-minutes"
            label={strings.minutesLabel}
            numeric
            onChange={setMinutesRaw}
            value={minutesRaw}
          />
          <SelectField<InterviewRecording>
            id="igg-recording"
            label={strings.recordingLabel}
            onChange={(value) => set("recording", value)}
            options={[
              { value: "audio", label: strings.recordingAudioOption },
              { value: "video", label: strings.recordingVideoOption },
              { value: "none", label: strings.recordingNoneOption },
            ]}
            value={input.recording}
          />
        </div>
      </Panel>

      <Panel title={strings.topicsPanel}>
        {input.topics.map((topic, index) => {
          const number = fmt(index + 1);
          return (
            <div
              className="space-y-3 border-border border-b pb-4 last:border-b-0 last:pb-0"
              key={topic.id}
            >
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <TextField
                    id={`igg-topic-${topic.id}`}
                    label={t(strings.topicNameLabel, { number })}
                    onChange={(value) => updateTopic(topic.id, { name: value })}
                    placeholder={strings.topicNamePlaceholder}
                    value={topic.name}
                  />
                </div>
                <button
                  aria-label={t(strings.removeTopicLabel, { number })}
                  className={BUTTON_SMALL}
                  disabled={input.topics.length <= 1}
                  onClick={() => removeTopic(topic.id)}
                  type="button"
                >
                  {strings.removeTopic}
                </button>
              </div>
              <TextAreaField
                id={`igg-questions-${topic.id}`}
                label={t(strings.topicQuestionsLabel, { number })}
                onChange={(value) =>
                  updateTopic(topic.id, { questions: value })
                }
                placeholder={strings.topicQuestionsPlaceholder}
                prose
                rows={3}
                value={topic.questions}
              />
            </div>
          );
        })}
        <button
          className={BUTTON_SECONDARY}
          disabled={input.topics.length >= MAX_TOPICS}
          onClick={addTopic}
          type="button"
        >
          {strings.addTopic}
        </button>
      </Panel>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-3`}>
        <h2 className={WIDGET_H2}>{strings.summaryHeading}</h2>
        <p className="text-foreground text-sm">
          {per === null
            ? counted
            : t(strings.summaryTime, {
                questions: counted,
                minutes: fmt(validMinutes ?? 0),
                per: tPlural(strings.perQuestion, locale, per, {
                  count: fmt(per, { maximumFractionDigits: 1 }),
                }),
              })}
        </p>
        {checks.length === 0 ? (
          <p className="text-muted-foreground text-sm">{strings.checksClear}</p>
        ) : (
          <div className="space-y-2 text-sm">
            <p className="text-foreground">{strings.checksLead}</p>
            <ul className="space-y-2">
              {checks.map((check) => (
                <li
                  className="rounded-lg border border-border bg-card p-3"
                  key={check.question}
                >
                  <p className="font-medium text-foreground">
                    {check.question}
                  </p>
                  <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                    {check.issues.map((issue) => (
                      <li key={issue}>{strings.issues[issue]}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.previewHeading}</h2>
        <DocumentActions
          filename="interview-guide"
          html={docToHtml(doc)}
          labels={strings}
          markdown={docToMarkdown(doc)}
          text={text}
        />
        <DocumentPreview doc={doc} label={strings.previewLabel} />
      </section>
    </div>
  );
}
